import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, Users } from 'lucide-react';

// Simulated real-time viewing data (in production, this would come from websockets/backend)
const getViewerCount = (vehicleId) => {
  // Generate a consistent but "random" number based on vehicle ID
  const hash = vehicleId?.split('').reduce((a, b) => ((a << 5) - a) + b.charCodeAt(0), 0) || 0;
  const base = Math.abs(hash % 8) + 1; // 1-8 base viewers
  
  // Add some randomness for "live" feel
  const timeVariation = Math.floor(Date.now() / 30000) % 3; // Changes every 30 seconds
  return base + timeVariation;
};

export function LiveViewers({ vehicleId, variant = 'badge' }) {
  const { t } = useTranslation();
  const [viewers, setViewers] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    // Initial count
    setViewers(getViewerCount(vehicleId));

    // Update periodically for "live" effect
    const interval = setInterval(() => {
      const newCount = getViewerCount(vehicleId);
      if (newCount !== viewers) {
        setIsAnimating(true);
        setViewers(newCount);
        setTimeout(() => setIsAnimating(false), 300);
      }
    }, 30000); // Update every 30 seconds

    return () => clearInterval(interval);
  }, [vehicleId]);

  if (variant === 'badge') {
    return (
      <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 bg-red-500/90 text-white text-xs font-medium rounded-full backdrop-blur-sm transition-transform ${isAnimating ? 'scale-110' : ''}`}>
        <Eye className="h-3 w-3 animate-pulse" />
        <span>{viewers} {t('liveViewers.watching')}</span>
      </div>
    );
  }

  if (variant === 'inline') {
    return (
      <div className={`flex items-center gap-1.5 text-orange-600 text-sm font-medium transition-transform ${isAnimating ? 'scale-105' : ''}`}>
        <Eye className="h-4 w-4" />
        <span>{viewers} {t('liveViewers.watching')}</span>
      </div>
    );
  }

  // Card variant - for vehicle cards with tooltip
  return (
    <div 
      className={`absolute top-2 left-2 z-10 flex items-center gap-1 px-2 py-1 bg-black/70 text-white text-xs rounded-md backdrop-blur-sm transition-all cursor-help group/viewers ${isAnimating ? 'scale-105' : ''}`}
      title={t('liveViewers.watching')}
    >
      <span className="relative flex h-2 w-2">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
        <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500"></span>
      </span>
      <Eye className="h-3 w-3" />
      <span>{viewers}</span>
      
      {/* Tooltip */}
      <div className="absolute left-0 top-full mt-1 opacity-0 group-hover/viewers:opacity-100 transition-opacity duration-200 pointer-events-none">
        <div className="bg-slate-900 text-white text-[10px] px-2 py-1 rounded shadow-lg whitespace-nowrap">
          {viewers} {t('liveViewers.watching')}
        </div>
      </div>
    </div>
  );
}

// Component to show total site visitors
export function TotalSiteViewers() {
  const [totalViewers, setTotalViewers] = useState(0);

  useEffect(() => {
    // Generate realistic total based on time of day
    const hour = new Date().getHours();
    let base = 15; // Base viewers
    
    // More viewers during business hours
    if (hour >= 9 && hour <= 18) {
      base = 25 + Math.floor(Math.random() * 15);
    } else if (hour >= 18 && hour <= 22) {
      base = 20 + Math.floor(Math.random() * 10);
    }
    
    setTotalViewers(base);

    const interval = setInterval(() => {
      setTotalViewers(prev => {
        const change = Math.random() > 0.5 ? 1 : -1;
        return Math.max(10, Math.min(50, prev + change));
      });
    }, 45000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="flex items-center gap-2 text-sm text-slate-600">
      <Users className="h-4 w-4 text-green-500" />
      <span><strong className="text-green-600">{totalViewers}</strong> visiteurs en ligne</span>
    </div>
  );
}

export default LiveViewers;
